/* v3.21-1641 · Global Compose Listener.
 *
 * The `hub:open-compose` CustomEvent (see `lib/comm_bus.js`) is
 * dispatched from every "Send Email" launch point that carries a known
 * client context — Client Detail, Client Workspace, Ghost surfaces,
 * NotificationCenter, CommunicationContactsPage, etc.  Before this
 * component existed the event fired into the void; nothing listened
 * and the composer never opened.
 *
 * This listener is mounted ONCE at the DashboardLayout level.  When an
 * event arrives it:
 *   1. Records the initial recipient, subject, and client_id / deal_id
 *      exactly as the caller passed them.  If the caller only sent a
 *      `client_id` (no `to`), we resolve the client's authoritative
 *      email via `GET /api/clients/{id}` so the composer opens with
 *      the primary email pre-populated.
 *   2. Opens the shared `ComposeAndApprove` composer.  From here on
 *      the operator owns the To field — no force-resetting.
 *
 * The global Email Center's "New Message" button does NOT dispatch
 * this event, so it keeps its existing blank-To behavior.
 */
import { useEffect, useState } from 'react';
import { onCompose } from '../lib/comm_bus';
import api from '../lib/api';
import ComposeAndApprove from './ComposeAndApprove';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ClientContextComposeListener() {
  const [open, setOpen] = useState(false);
  const [initial, setInitial] = useState(null);

  useEffect(() => {
    return onCompose(async (detail = {}) => {
      let toAddr = (detail.to || '').trim();
      let clientId = detail.client_id || null;
      let contactId = detail.contact_id || null;
      let subject = detail.subject || '';

      // If the caller only told us WHICH client we're emailing (no
      // literal `to` address), pull the authoritative primary email
      // from the client record.  This is the "Send Email from Client
      // Detail / Client Workspace with the client's email pre-loaded"
      // path the operator asked for.
      if (!toAddr && clientId) {
        try {
          const { data: cli } = await api.get(`/clients/${clientId}`);
          const candidate = (cli?.email || '').trim();
          if (EMAIL_RE.test(candidate)) toAddr = candidate;
        } catch (_e) {
          /* client not accessible — open composer with empty To */
        }
      }

      // Only keep a valid, non-empty address; never populate undefined
      // / null / placeholder strings.
      const initialTo = EMAIL_RE.test(toAddr) ? [toAddr] : [];

      // Deal-scoped CC / recipient augmentation (rep, director, VO,
      // concierge) uses the same authoritative endpoint every other
      // wrapper (`EmailComposeDialog`, `SendEmailModal`) reads.
      let cc = [];
      if (clientId) {
        try {
          const r = await api.get(`/clients/${clientId}/email-recipients`);
          cc = Array.isArray(r.data?.cc) ? r.data.cc.filter(Boolean) : [];
        } catch (_e) { /* fall through — backend also auto-augments */ }
      }

      setInitial({
        template_key: 'blank',
        subject,
        body:         '',
        to:           initialTo,
        cc,
        client_id:    clientId,
        contact_id:   contactId,
        context:      {},
      });
      setOpen(true);
    });
  }, []);

  if (!open || !initial) return null;

  return (
    <ComposeAndApprove
      open={open}
      onOpenChange={(v) => setOpen(v)}
      initial={initial}
      onSent={() => setOpen(false)}
    />
  );
}
